/**
 * Data access for the whole app.
 *
 * Every function has two implementations behind one signature: a real Supabase
 * query, and the same shape served from the bundled demo dataset. Screens never
 * branch on which one is live, so pointing the app at a real project changes
 * nothing above this file. That includes the writes: the demo path is a
 * stateful in-memory store (lib/mock/store.ts) that mirrors the triggers and
 * policies in supabase/migrations, so tapping through the app in demo mode
 * behaves the way it will once Postgres is behind it.
 *
 * Two rules this file follows:
 *
 *  - The qualification gate is never re-implemented here. Staffing reads the
 *    event_eligible_coaches view (or its demo mirror), and an assignment is
 *    refused unless the coach is in that list.
 *  - Nothing writes earnings.is_visible_to_coach. Coach-facing money stays shut
 *    until Pascal signs the pay grid off and a shadow month reconciles.
 *
 * A handful of pure predicates are imported from lib/mock/store: they are the
 * single copy of a rule that also exists in SQL (coach_can_see_event), and no
 * demo state travels with them.
 */

import { isSupabaseConfigured, requireSupabase, unwrap } from './supabase'
import {
  buildAvailabilityRequestNotification,
  buildQualificationApprovedNotification,
  buildQualificationDecisionNotification,
  type NewNotification,
} from './mock/notify'
import * as demoStore from './mock/store'
import type { DemoTables } from './mock/data'
import type {
  Assignment,
  Availability,
  AvailabilityResponse,
  ChecklistItem,
  ChecklistItemState,
  CoachServiceReadiness,
  Earning,
  EventEligibleCoach,
  EventRow,
  MaterialsCheck,
  ModuleCompletion,
  NotificationChaseRow,
  NotificationQueueRow,
  NotificationType,
  PostEventReport,
  Profile,
  Qualification,
  Sanction,
  Service,
  TrainingModule,
} from '../types/db'

/** True when the app is running on the bundled demo dataset. */
export const isDemoMode = !isSupabaseConfigured

/* -------------------------------------------------------------------------- */
/* View models                                                                */
/* -------------------------------------------------------------------------- */

export interface EventWithService extends EventRow {
  service: Service
}

/** One row on a coach's schedule: the event plus this coach's relationship to it. */
export interface CoachEventCard {
  event: EventWithService
  assignment: Assignment | null
  availability: Availability | null
}

/** One row in the admin events list. */
export interface AdminEventCard {
  event: EventWithService
  assignedCount: number
  respondedCount: number
  pendingCount: number
}

/** How full an event is. Drives the "2 of 3 coaches" line and the assign screen. */
export interface EventStaffing {
  required: number
  assigned: number
  confirmed: number
  remaining: number
  isFull: boolean
  availableCount: number
  tentativeCount: number
  unansweredCount: number
}

export type MaterialsCheckWithItems = MaterialsCheck & { items: ChecklistItem[] }

export interface EventDetail {
  event: EventWithService
  assignments: Array<Assignment & { coach: Profile }>
  availabilities: Array<Availability & { coach: Profile }>
  materials: MaterialsCheckWithItems[]
  reports: Array<PostEventReport & { coach: Profile }>
  staffing: EventStaffing
}

export interface EligibleCoach {
  coach: Profile
  qualification_id: string
  availability_response: AvailabilityResponse | null
  responded_at: string | null
  is_assigned: boolean
  assignment_id: string | null
}

export interface TrainingProgress {
  modules: Array<TrainingModule & { completion: ModuleCompletion | null }>
  readiness: CoachServiceReadiness[]
  /**
   * The coach's own qualification rows. The readiness view stops at the status,
   * so this is where a screen finds the reason behind a decline or withdrawal
   * (revoke_reason) and who took the decision.
   */
  qualifications: Qualification[]
}

export interface EarningsSummary {
  /** False while the shadow-month gate is closed, which is every row today. */
  visible: boolean
  months: Array<{
    period_month: string
    total_aed: number
    rows: Array<Earning & { event: EventWithService }>
  }>
  containsPlaceholderRates: boolean
}

export interface PendingQualification extends Qualification {
  coach: Profile
  service: Service
  readiness: CoachServiceReadiness | null
}

export interface AdminDashboard {
  eventsNeedingCoaches: AdminEventCard[]
  upcomingCount: number
  pendingApprovals: number
  unacknowledgedReminders: number
  activeCoaches: number
  candidateCoaches: number
}

/** What a coach still owes a post-event report on. */
export interface ReportDue {
  event: EventWithService
  assignment: Assignment
}

/* -------------------------------------------------------------------------- */
/* Demo helpers                                                               */
/* -------------------------------------------------------------------------- */

/** A fresh deep copy of the demo tables. Never the live objects. */
const demo = (): DemoTables => demoStore.snapshot()

const byId = <T extends { id: string }>(rows: T[], id: string): T | undefined =>
  rows.find((r) => r.id === id)

function withService(db: DemoTables, e: EventRow): EventWithService {
  const service = byId(db.services, e.service_id)
  if (!service) throw new Error(`Demo data is inconsistent: no service for event ${e.id}`)
  return { ...e, service }
}

function coachOf(db: DemoTables, id: string): Profile {
  const p = byId(db.profiles, id)
  if (!p) throw new Error(`Demo data is inconsistent: no profile ${id}`)
  return p
}

function sortByStart<T extends { starts_at: string }>(rows: T[], dir: 1 | -1 = 1): T[] {
  return [...rows].sort(
    (a, b) => dir * (new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()),
  )
}

const EVENT_SELECT = '*, service:services(*)'

/* -------------------------------------------------------------------------- */
/* Pure rules, shared by both backends                                        */
/* -------------------------------------------------------------------------- */

/** Counts an event's staffing from its assignment and availability rows. */
export function summariseStaffing(
  event: Pick<EventRow, 'coaches_required'>,
  assignments: Array<Pick<Assignment, 'status'>>,
  availabilities: Array<Pick<Availability, 'response'>>,
): EventStaffing {
  const active = assignments.filter((a) => a.status !== 'cancelled')
  const assigned = active.length
  return {
    required: event.coaches_required,
    assigned,
    confirmed: active.filter((a) => a.status === 'confirmed' || a.status === 'completed').length,
    remaining: Math.max(0, event.coaches_required - assigned),
    isFull: assigned >= event.coaches_required,
    availableCount: availabilities.filter((a) => a.response === 'available').length,
    tentativeCount: availabilities.filter((a) => a.response === 'tentative').length,
    unansweredCount: availabilities.filter((a) => a.response === 'pending').length,
  }
}

/**
 * Whether a coach may still change their answer. Once Michel has assigned them
 * the answer is no longer the mechanism: they confirm, or they stand down and
 * tell him why. The database does not enforce this, so the screen should.
 */
export function canChangeAvailability(card: Pick<CoachEventCard, 'event' | 'assignment'>): boolean {
  if (card.assignment && card.assignment.status !== 'cancelled') return false
  if (card.event.status === 'cancelled' || card.event.cancelled_at) return false
  return new Date(card.event.starts_at).getTime() > Date.now()
}

/**
 * Modules finished makes a coach ELIGIBLE. This is that check, and nothing
 * more: being eligible is not being qualified, which only Michel's approval
 * grants.
 */
export function canRequestQualification(readiness: CoachServiceReadiness | null): boolean {
  if (!readiness) return false
  if (readiness.qualification_status === 'approved') return false
  if (readiness.qualification_status === 'pending') return false
  return readiness.modules_complete
}

/**
 * A WhatsApp deep link with the message pre-filled. This is a plain URL, not
 * the WhatsApp Business API: the chase list stays a human paste, by decision.
 */
export function whatsappUrl(phone: string, text: string): string {
  return `https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(text)}`
}

function rankAvailability(row: EligibleCoach): number {
  if (row.is_assigned) return 5
  switch (row.availability_response) {
    case 'available':
      return 4
    case 'tentative':
      return 3
    case 'pending':
      return 2
    case 'unavailable':
      return 0
    default:
      return 1
  }
}

/* -------------------------------------------------------------------------- */
/* Demo control                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Throw the demo story away and rebuild it from the seed, with the dates
 * recomputed from today's clock. Only meaningful on the bundled dataset.
 */
export async function resetDemoData(): Promise<void> {
  if (isSupabaseConfigured) {
    throw new Error('Reset is only available on the bundled demo dataset.')
  }
  demoStore.reset()
}

/**
 * Fires after every demo write. Returns an unsubscribe. On a real backend this
 * never fires, which is correct: Postgres is the one telling you what changed.
 */
export function subscribeToDemoData(listener: () => void): () => void {
  if (isSupabaseConfigured) return () => {}
  return demoStore.subscribe(listener)
}

/* -------------------------------------------------------------------------- */
/* Profile and reference data                                                 */
/* -------------------------------------------------------------------------- */

export async function getProfile(userId: string): Promise<Profile | null> {
  if (!isSupabaseConfigured) {
    return byId(demo().profiles, userId) ?? null
  }
  const { data, error } = await requireSupabase()
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle()
  if (error) throw new Error(error.message)
  return (data as Profile | null) ?? null
}

export async function listServices(): Promise<Service[]> {
  if (!isSupabaseConfigured) {
    return demo()
      .services.filter((s) => s.is_active)
      .sort((a, b) => a.sort_order - b.sort_order)
  }
  const res = await requireSupabase()
    .from('services')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  return unwrap(res) as Service[]
}

/* -------------------------------------------------------------------------- */
/* Coach: schedule                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Everything a coach can see, which is the product's central rule:
 * events they are assigned to, events they were asked about, and events for a
 * service they are qualified for while those are still live. RLS enforces the
 * same three-way rule server side through coach_can_see_event().
 */
export async function listCoachEvents(coachId: string): Promise<CoachEventCard[]> {
  const cards = new Map<string, CoachEventCard>()

  if (!isSupabaseConfigured) {
    const db = demo()
    for (const a of db.assignments) {
      if (a.coach_id !== coachId || a.status === 'cancelled') continue
      const e = byId(db.events, a.event_id)
      if (!e) continue
      cards.set(e.id, { event: withService(db, e), assignment: a, availability: null })
    }
    for (const av of db.availabilities) {
      if (av.coach_id !== coachId) continue
      const e = byId(db.events, av.event_id)
      if (!e) continue
      const existing = cards.get(e.id)
      if (existing) existing.availability = av
      else cards.set(e.id, { event: withService(db, e), assignment: null, availability: av })
    }
    for (const e of db.events) {
      if (cards.has(e.id)) continue
      if (!demoStore.isEventOpenToQualifiedCoach(e)) continue
      if (!demoStore.isCoachQualified(db, coachId, e.service_id)) continue
      cards.set(e.id, { event: withService(db, e), assignment: null, availability: null })
    }
    return sortCards(cards)
  }

  const sb = requireSupabase()
  const [qualRes, eventRes, assignRes, availRes] = await Promise.all([
    sb
      .from('qualifications')
      .select('service_id')
      .eq('coach_id', coachId)
      .eq('status', 'approved')
      .is('revoked_at', null),
    // RLS already narrows this to coach_can_see_event(). The client-side filter
    // below matters only for the core team, whose policy returns everything.
    sb.from('events').select(EVENT_SELECT),
    sb.from('assignments').select('*').eq('coach_id', coachId).neq('status', 'cancelled'),
    sb.from('availabilities').select('*').eq('coach_id', coachId),
  ])
  for (const r of [qualRes, eventRes, assignRes, availRes]) {
    if (r.error) throw new Error(r.error.message)
  }

  const qualifiedServices = new Set(
    ((qualRes.data ?? []) as Array<{ service_id: string }>).map((q) => q.service_id),
  )
  const events = (eventRes.data ?? []) as unknown as EventWithService[]
  const eventsById = new Map(events.map((e) => [e.id, e]))

  for (const assignment of ((assignRes.data ?? []) as Assignment[]).map(withCoachConfirmation)) {
    const event = eventsById.get(assignment.event_id)
    if (event) cards.set(event.id, { event, assignment, availability: null })
  }
  for (const availability of (availRes.data ?? []) as Availability[]) {
    const event = eventsById.get(availability.event_id)
    if (!event) continue
    const existing = cards.get(event.id)
    if (existing) existing.availability = availability
    else cards.set(event.id, { event, assignment: null, availability })
  }
  for (const event of events) {
    if (cards.has(event.id)) continue
    if (!qualifiedServices.has(event.service_id)) continue
    if (!demoStore.isEventOpenToQualifiedCoach(event)) continue
    cards.set(event.id, { event, assignment: null, availability: null })
  }
  return sortCards(cards)
}

function sortCards(cards: Map<string, CoachEventCard>): CoachEventCard[] {
  return [...cards.values()].sort(
    (a, b) => new Date(a.event.starts_at).getTime() - new Date(b.event.starts_at).getTime(),
  )
}

export function splitSchedule(cards: CoachEventCard[], now = new Date()) {
  const t = now.getTime()
  const upcoming = cards.filter(
    (c) => new Date(c.event.starts_at).getTime() >= t && c.event.status !== 'cancelled',
  )
  const past = cards.filter((c) => new Date(c.event.starts_at).getTime() < t).reverse()
  const cancelled = cards.filter(
    (c) => c.event.status === 'cancelled' && new Date(c.event.starts_at).getTime() >= t,
  )
  const needsAnswer = upcoming.filter(
    (c) => !c.assignment && (c.availability === null || c.availability.response === 'pending'),
  )
  const needsConfirm = upcoming.filter((c) => c.assignment?.status === 'assigned')
  return { upcoming, past, cancelled, needsAnswer, needsConfirm }
}

/**
 * Events this coach worked and has not filed a report on. This is what the
 * post-event questionnaire prompt is driven from.
 */
export async function listReportsDue(coachId: string, now = new Date()): Promise<ReportDue[]> {
  const [cards, reports] = await Promise.all([
    listCoachEvents(coachId),
    listOwnReports(coachId),
  ])
  const reported = new Set(reports.map((r) => r.event_id))
  return cards
    .filter((c) => c.assignment && c.assignment.status !== 'cancelled')
    .filter((c) => c.event.status !== 'cancelled')
    .filter((c) => new Date(c.event.ends_at).getTime() < now.getTime())
    .filter((c) => !reported.has(c.event.id))
    .map((c) => ({ event: c.event, assignment: c.assignment as Assignment }))
    .sort((a, b) => b.event.starts_at.localeCompare(a.event.starts_at))
}

async function listOwnReports(coachId: string): Promise<PostEventReport[]> {
  if (!isSupabaseConfigured) {
    return demo().postEventReports.filter((r) => r.coach_id === coachId)
  }
  const res = await requireSupabase()
    .from('post_event_reports')
    .select('*')
    .eq('coach_id', coachId)
  return unwrap(res) as PostEventReport[]
}

/* -------------------------------------------------------------------------- */
/* Event detail                                                               */
/* -------------------------------------------------------------------------- */

export async function getEventDetail(eventId: string): Promise<EventDetail | null> {
  if (!isSupabaseConfigured) {
    const db = demo()
    const e = byId(db.events, eventId)
    if (!e) return null
    const materials = db.materialsChecks
      .filter((m) => m.event_id === eventId)
      .sort((a, b) => (a.phase === b.phase ? 0 : a.phase === 'check_out' ? -1 : 1))
      .map((m) => ({
        ...m,
        items: db.checklistItems
          .filter((i) => i.materials_check_id === m.id)
          .sort((a, b) => a.sort_order - b.sort_order),
      }))
    const assignments = db.assignments
      .filter((a) => a.event_id === eventId)
      .map((a) => ({ ...a, coach: coachOf(db, a.coach_id) }))
    const availabilities = db.availabilities
      .filter((a) => a.event_id === eventId)
      .map((a) => ({ ...a, coach: coachOf(db, a.coach_id) }))
    return {
      event: withService(db, e),
      assignments,
      availabilities,
      materials,
      reports: db.postEventReports
        .filter((r) => r.event_id === eventId)
        .map((r) => ({ ...r, coach: coachOf(db, r.coach_id) })),
      staffing: summariseStaffing(e, assignments, availabilities),
    }
  }

  const sb = requireSupabase()
  const eventRes = await sb.from('events').select(EVENT_SELECT).eq('id', eventId).maybeSingle()
  if (eventRes.error) throw new Error(eventRes.error.message)
  if (!eventRes.data) return null

  const [assignRes, availRes, checkRes, reportRes] = await Promise.all([
    sb.from('assignments').select('*, coach:profiles!coach_id(*)').eq('event_id', eventId),
    sb.from('availabilities').select('*, coach:profiles!coach_id(*)').eq('event_id', eventId),
    sb
      .from('materials_checks')
      .select('*, items:checklist_items(*)')
      .eq('event_id', eventId)
      .order('phase'),
    sb.from('post_event_reports').select('*, coach:profiles(*)').eq('event_id', eventId),
  ])
  for (const r of [assignRes, availRes, checkRes, reportRes]) {
    if (r.error) throw new Error(r.error.message)
  }
  const event = eventRes.data as unknown as EventWithService
  const assignments = ((assignRes.data ?? []) as unknown as EventDetail['assignments']).map(
    withCoachConfirmation,
  )
  const availabilities = (availRes.data ?? []) as unknown as EventDetail['availabilities']
  const materials = ((checkRes.data ?? []) as unknown as MaterialsCheckWithItems[]).map((m) => ({
    ...m,
    items: [...(m.items ?? [])].sort((a, b) => a.sort_order - b.sort_order),
  }))
  return {
    event,
    assignments,
    availabilities,
    materials,
    reports: (reportRes.data ?? []) as unknown as EventDetail['reports'],
    staffing: summariseStaffing(event, assignments, availabilities),
  }
}

/* -------------------------------------------------------------------------- */
/* Coach: availability and assignment                                         */
/* -------------------------------------------------------------------------- */

/**
 * Whether a coach is allowed to open an event at all: on it, asked about it, or
 * qualified for it while it is still live. On the live backend RLS answers this
 * by returning no row from getEventDetail(), so this is always true there; the
 * demo store has no RLS, so the screen asks first. Keeps a birthday-only coach
 * from reading a swimming brief by typing the URL.
 */
export async function canCoachSeeEvent(eventId: string, coachId: string): Promise<boolean> {
  if (!isSupabaseConfigured) {
    return demoStore.coachCanSeeEvent(demo(), eventId, coachId)
  }
  return true
}

/**
 * Answer "can you work this?", or change an answer already given. A coach may
 * also answer an event nobody asked them about, as long as they are qualified
 * for it: that is the volunteering path, and RLS allows exactly the same thing
 * through coach_can_see_event().
 */
export async function respondToAvailability(
  eventId: string,
  coachId: string,
  response: AvailabilityResponse,
  note: string | null = null,
): Promise<Availability> {
  if (!isSupabaseConfigured) {
    return demoStore.respondToAvailability(eventId, coachId, response, note)
  }
  const res = await requireSupabase()
    .from('availabilities')
    .upsert(
      {
        event_id: eventId,
        coach_id: coachId,
        response,
        note,
        // responded_at is deliberately absent. trg_availabilities_stamp sets it,
        // and only when the answer actually changed, so re-saving the same
        // answer keeps the original timestamp. Sending it here would overwrite
        // that on every save and drift from the demo store.
      },
      { onConflict: 'event_id,coach_id' },
    )
    .select()
    .single()
  if (res.error) throw new Error(res.error.message)
  return res.data as Availability
}

/**
 * Ask the qualified coaches whether they can work an event. Only coaches on the
 * eligible list are asked, which is the gate doing its job: a coach who is not
 * qualified for the service is never contacted about it.
 *
 * Returns how many coaches were NEWLY asked. Somebody who was already asked, or
 * who has already answered, is not asked twice and is not counted, so tapping
 * "ask the team" a second time honestly reports 0 rather than claiming to have
 * sent a round of requests it did not send.
 */
export async function requestAvailability(
  eventId: string,
  coachIds: string[],
  requestedBy: string,
): Promise<number> {
  const eligible = await listEligibleCoaches(eventId)
  const allowed = coachIds.filter((id) => eligible.some((e) => e.coach.id === id))
  if (allowed.length === 0) return 0

  if (!isSupabaseConfigured) {
    return demoStore.requestAvailability(eventId, allowed, requestedBy)
  }

  const sb = requireSupabase()
  const at = new Date().toISOString()
  const detail = await sb.from('events').select(EVENT_SELECT).eq('id', eventId).single()
  if (detail.error) throw new Error(detail.error.message)
  const event = detail.data as unknown as EventWithService

  // ignoreDuplicates makes this ON CONFLICT DO NOTHING, so an answer somebody
  // has already given is never reset to pending. The select then returns only
  // the rows that were actually inserted, which is the count worth reporting.
  const insertRes = await sb
    .from('availabilities')
    .upsert(
      allowed.map((coachId) => ({
        event_id: eventId,
        coach_id: coachId,
        response: 'pending' as const,
        requested_at: at,
        requested_by: requestedBy,
      })),
      { onConflict: 'event_id,coach_id', ignoreDuplicates: true },
    )
    .select('id')
  if (insertRes.error) throw new Error(insertRes.error.message)

  // The notice is deduped on its own key, so re-asking never double-notifies.
  await enqueueAppNotifications(
    allowed.map((coachId) =>
      buildAvailabilityRequestNotification(coachId, event, event.service, at),
    ),
  )
  return (insertRes.data ?? []).length
}

/**
 * Live rows only. trg_assignments_guard (migration 02) stops a coach writing
 * any status but 'cancelled', so the coach's own confirmation is confirmed_at
 * alone and the status column stays 'assigned'. Read back, that combination is
 * a confirmation, so every screen keeps keying on status exactly as it does on
 * the demo store. Rows the backend confirmed itself already carry 'confirmed'
 * and pass through untouched.
 */
function withCoachConfirmation<T extends Pick<Assignment, 'status' | 'confirmed_at'>>(row: T): T {
  return row.status === 'assigned' && row.confirmed_at ? ({ ...row, status: 'confirmed' } as T) : row
}

/**
 * The coach taps "I will be there".
 *
 * On the live backend this stamps confirmed_at only: trg_assignments_guard
 * refuses a status change by a non-approver except to 'cancelled', and the
 * schema is locked for this phase. withCoachConfirmation() turns the stamp back
 * into status 'confirmed' on every live read, so the schedule, the event page
 * and Michel's staffing view all agree. The clean fix stays a later migration:
 * widen the guard to allow own-row 'assigned' to 'confirmed', or add a
 * SECURITY DEFINER confirm_assignment(uuid) RPC that checks coach_id = auth.uid().
 */
export async function confirmAssignment(assignmentId: string): Promise<Assignment> {
  if (!isSupabaseConfigured) {
    return demoStore.confirmAssignment(assignmentId)
  }
  const res = await requireSupabase()
    .from('assignments')
    .update({ confirmed_at: new Date().toISOString() })
    .eq('id', assignmentId)
    .select()
    .single()
  if (res.error) throw new Error(res.error.message)
  return withCoachConfirmation(res.data as Assignment)
}

/**
 * The coach stands themselves down. This is the only assignment change a coach
 * is allowed to make: trg_assignments_guard refuses everything else.
 *
 * Cancelling inside 12 hours is what contract clause 4.3 is about. Detection
 * stays manual in v1 and no sanction is written here: only 6 of 22 contracts
 * are signed, so automating it would apply it unevenly.
 */
export async function withdrawFromAssignment(
  assignmentId: string,
  reason: string,
): Promise<Assignment> {
  const row = await cancelAssignmentRow(assignmentId, reason)
  // Their answer on the board must not keep saying "available" after they have
  // just said they cannot make it, or Michel re-assigns them off a stale yes.
  // The reason travels as the note. Own-row upsert, which RLS allows a coach.
  await respondToAvailability(row.event_id, row.coach_id, 'unavailable', reason)
  return row
}

async function cancelAssignmentRow(assignmentId: string, reason: string): Promise<Assignment> {
  if (!isSupabaseConfigured) {
    return demoStore.cancelAssignment(assignmentId, reason)
  }
  const res = await requireSupabase()
    .from('assignments')
    .update({
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      cancellation_reason: reason,
    })
    .eq('id', assignmentId)
    .select()
    .single()
  if (res.error) throw new Error(res.error.message)
  return res.data as Assignment
}

/* -------------------------------------------------------------------------- */
/* Coach: training                                                            */
/* -------------------------------------------------------------------------- */

export async function getTrainingProgress(coachId: string): Promise<TrainingProgress> {
  if (!isSupabaseConfigured) {
    const db = demo()
    const completions = db.moduleCompletions.filter((c) => c.coach_id === coachId)
    const modules = [...db.trainingModules]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((m) => ({
        ...m,
        completion: completions.find((c) => c.module_id === m.id) ?? null,
      }))

    const doneIds = new Set(completions.map((c) => c.module_id))
    const readiness: CoachServiceReadiness[] = db.services
      .filter((s) => s.is_active)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((s) => {
        const required = db.serviceModuleRequirements.filter(
          (r) => r.service_id === s.id && r.is_mandatory,
        )
        const missing = required.filter((r) => !doneIds.has(r.module_id))
        const qual = db.qualifications.find(
          (q) => q.coach_id === coachId && q.service_id === s.id,
        )
        return {
          coach_id: coachId,
          service_id: s.id,
          service_code: s.code,
          service_name: s.name,
          modules_required: required.length,
          modules_completed: required.length - missing.length,
          modules_complete: missing.length === 0,
          missing_module_codes: missing
            .map((r) => byId(db.trainingModules, r.module_id)?.code)
            .filter((code): code is TrainingModule['code'] => code !== undefined),
          qualification_status: qual?.status ?? null,
          qualification_approved_at: qual?.approved_at ?? null,
        }
      })
    const qualifications = db.qualifications.filter((q) => q.coach_id === coachId)
    return { modules, readiness, qualifications }
  }

  const sb = requireSupabase()
  const [moduleRes, completionRes, readinessRes, qualificationRes] = await Promise.all([
    sb.from('training_modules').select('*').order('sort_order'),
    sb.from('module_completions').select('*').eq('coach_id', coachId),
    sb.from('coach_service_readiness').select('*').eq('coach_id', coachId),
    // qualifications_select_own: a coach reads their own rows, reason included.
    sb.from('qualifications').select('*').eq('coach_id', coachId),
  ])
  for (const r of [moduleRes, completionRes, readinessRes, qualificationRes]) {
    if (r.error) throw new Error(r.error.message)
  }
  const completions = (completionRes.data ?? []) as ModuleCompletion[]
  return {
    modules: ((moduleRes.data ?? []) as TrainingModule[]).map((m) => ({
      ...m,
      completion: completions.find((c) => c.module_id === m.id) ?? null,
    })),
    readiness: (readinessRes.data ?? []) as CoachServiceReadiness[],
    qualifications: (qualificationRes.data ?? []) as Qualification[],
  }
}

/**
 * Records an in-app module completion. Idempotent: finishing a module twice is
 * one row, because module_completions is unique on (coach, module).
 */
export async function markModuleComplete(coachId: string, moduleId: string): Promise<void> {
  if (!isSupabaseConfigured) {
    demoStore.markModuleComplete(coachId, moduleId)
    return
  }
  const { error } = await requireSupabase()
    .from('module_completions')
    .upsert(
      { coach_id: coachId, module_id: moduleId, source: 'in_app' },
      { onConflict: 'coach_id,module_id', ignoreDuplicates: true },
    )
  if (error) throw new Error(error.message)
}

/**
 * Ask Michel for a qualification.
 *
 * The rule, enforced here because the database cannot express it: finishing the
 * required modules makes a coach ELIGIBLE, and this is the request that eligible
 * coach raises. Approval is a separate, human act.
 */
export async function requestQualification(coachId: string, serviceId: string): Promise<void> {
  const progress = await getTrainingProgress(coachId)
  const readiness = progress.readiness.find((r) => r.service_id === serviceId)
  if (!readiness) {
    throw new Error('That service is not taking requests right now.')
  }
  if (readiness.qualification_status === 'approved') {
    throw new Error(`You are already qualified for ${readiness.service_name}.`)
  }
  if (!readiness.modules_complete) {
    const missing = readiness.missing_module_codes
      .map((code) => progress.modules.find((m) => m.code === code)?.title ?? code)
      .join(', ')
    throw new Error(`Finish the training for this service first. Still to do: ${missing}.`)
  }

  if (!isSupabaseConfigured) {
    demoStore.requestQualification(coachId, serviceId)
    return
  }
  const { error } = await requireSupabase()
    .from('qualifications')
    .upsert(
      {
        coach_id: coachId,
        service_id: serviceId,
        status: 'pending',
        requested_at: new Date().toISOString(),
      },
      { onConflict: 'coach_id,service_id' },
    )
  if (error) throw new Error(error.message)
}

/* -------------------------------------------------------------------------- */
/* Coach: earnings and record                                                 */
/* -------------------------------------------------------------------------- */

/**
 * A coach's own earnings.
 *
 * Today this returns nothing for every coach, on purpose:
 * earnings.is_visible_to_coach is false on every row and RLS reads that column,
 * so the screen shows the "pending finance approval" state rather than a number
 * nobody has agreed to. Nothing in this app flips that flag; it is a go-live
 * step that happens after Pascal signs the grid off and a shadow month
 * reconciles line by line.
 */
export async function getCoachEarnings(coachId: string): Promise<EarningsSummary> {
  let rows: Array<Earning & { event: EventWithService }>

  if (!isSupabaseConfigured) {
    const db = demo()
    rows = db.earnings
      .filter((e) => e.coach_id === coachId && e.is_visible_to_coach)
      .map((e) => {
        const event = byId(db.events, e.event_id)
        if (!event) throw new Error(`Demo data is inconsistent: no event for earning ${e.id}`)
        return { ...e, event: withService(db, event) }
      })
  } else {
    const res = await requireSupabase()
      .from('earnings')
      .select(`*, event:events(${EVENT_SELECT})`)
      .eq('coach_id', coachId)
      .order('period_month', { ascending: false })
    rows = unwrap(res) as unknown as Array<Earning & { event: EventWithService }>
  }

  const grouped = new Map<string, Array<Earning & { event: EventWithService }>>()
  for (const row of rows) {
    const list = grouped.get(row.period_month) ?? []
    list.push(row)
    grouped.set(row.period_month, list)
  }

  return {
    visible: rows.length > 0,
    containsPlaceholderRates: rows.some((r) => r.breakdown?.is_placeholder === true),
    months: [...grouped.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([period_month, monthRows]) => ({
        period_month,
        total_aed: monthRows.reduce(
          (sum, r) => sum + (r.status === 'void' ? 0 : Number(r.amount_aed)),
          0,
        ),
        rows: monthRows.sort(
          (a, b) => new Date(b.event.starts_at).getTime() - new Date(a.event.starts_at).getTime(),
        ),
      })),
  }
}

export async function getCoachSanctions(coachId: string): Promise<Sanction[]> {
  if (!isSupabaseConfigured) {
    return demo()
      .sanctions.filter((s) => s.coach_id === coachId)
      .sort((a, b) => b.issued_at.localeCompare(a.issued_at))
  }
  const res = await requireSupabase()
    .from('sanctions')
    .select('*')
    .eq('coach_id', coachId)
    .order('issued_at', { ascending: false })
  return unwrap(res) as Sanction[]
}

export async function acknowledgeSanction(sanctionId: string): Promise<void> {
  if (!isSupabaseConfigured) {
    demoStore.acknowledgeSanction(sanctionId)
    return
  }
  const { error } = await requireSupabase()
    .from('sanctions')
    .update({ acknowledged_at: new Date().toISOString() })
    .eq('id', sanctionId)
  if (error) throw new Error(error.message)
}

/* -------------------------------------------------------------------------- */
/* Materials: check-out and check-in                                          */
/* -------------------------------------------------------------------------- */

/** One item on the crate list. Gina and the assigned coach can both set it. */
export async function setChecklistItemState(
  itemId: string,
  state: ChecklistItemState,
  note: string | null = null,
  updatedBy: string | null = null,
): Promise<ChecklistItem> {
  if (!isSupabaseConfigured) {
    return demoStore.setChecklistItemState(itemId, state, note, updatedBy)
  }
  const res = await requireSupabase()
    .from('checklist_items')
    .update({ state, note, updated_by: updatedBy })
    .eq('id', itemId)
    .select()
    .single()
  if (res.error) throw new Error(res.error.message)
  return res.data as ChecklistItem
}

/** "Everything is here" in one tap, before signing the check off. */
export async function setAllChecklistItems(
  materialsCheckId: string,
  state: ChecklistItemState,
  updatedBy: string | null = null,
): Promise<number> {
  if (!isSupabaseConfigured) {
    return demoStore.setAllChecklistItems(materialsCheckId, state, updatedBy)
  }
  const res = await requireSupabase()
    .from('checklist_items')
    .update({ state, updated_by: updatedBy })
    .eq('materials_check_id', materialsCheckId)
    .select('id')
  if (res.error) throw new Error(res.error.message)
  return (res.data ?? []).length
}

export interface MaterialsCheckInput {
  materialsCheckId: string
  checkedBy: string
  /** True for "all present and correct", false when something is missing or damaged. */
  allAccountedFor: boolean
  /** Required when allAccountedFor is false: what is missing, and what happened. */
  note?: string | null
}

/**
 * Sign off a check-out or a check-in.
 *
 * All accounted for closes the check. Anything else lands as an issue with a
 * note, which is what Gina actually chases the next morning. A discrepancy
 * without an explanation is refused, because "something is missing" with no
 * detail is worse than no record at all.
 */
export async function completeMaterialsCheck(
  input: MaterialsCheckInput,
): Promise<MaterialsCheck> {
  const note = input.note?.trim() ? input.note.trim() : null
  if (!input.allAccountedFor && !note) {
    throw new Error('Say what is missing or damaged before you sign this off.')
  }

  if (!isSupabaseConfigured) {
    return demoStore.completeMaterialsCheck(
      input.materialsCheckId,
      input.checkedBy,
      input.allAccountedFor,
      note,
    )
  }

  const sb = requireSupabase()
  if (input.allAccountedFor) {
    const bulk = await sb
      .from('checklist_items')
      .update({ state: 'ok', updated_by: input.checkedBy })
      .eq('materials_check_id', input.materialsCheckId)
      .eq('state', 'pending')
    if (bulk.error) throw new Error(bulk.error.message)
  }

  const itemsRes = await sb
    .from('checklist_items')
    .select('state')
    .eq('materials_check_id', input.materialsCheckId)
  if (itemsRes.error) throw new Error(itemsRes.error.message)
  const hasProblem = ((itemsRes.data ?? []) as Array<{ state: ChecklistItemState }>).some(
    (i) => i.state === 'missing' || i.state === 'damaged',
  )

  const res = await sb
    .from('materials_checks')
    .update({
      status: input.allAccountedFor && !hasProblem ? 'complete' : 'issue',
      checked_by: input.checkedBy,
      checked_at: new Date().toISOString(),
      notes: note,
    })
    .eq('id', input.materialsCheckId)
    .select()
    .single()
  if (res.error) throw new Error(res.error.message)
  return res.data as MaterialsCheck
}

/* -------------------------------------------------------------------------- */
/* Post-event report                                                          */
/* -------------------------------------------------------------------------- */

export interface PostEventReportInput {
  eventId: string
  coachId: string
  /** 1 to 5. */
  overallRating: number | null
  attendanceCount: number | null
  wentWell: string | null
  issues: string | null
  incident: boolean
  incidentDetails: string | null
  clientFeedback: string | null
  photoPaths?: string[]
}

/** The report this coach already filed for this event, if any. */
export async function getPostEventReport(
  eventId: string,
  coachId: string,
): Promise<PostEventReport | null> {
  if (!isSupabaseConfigured) {
    return (
      demo().postEventReports.find(
        (r) => r.event_id === eventId && r.coach_id === coachId,
      ) ?? null
    )
  }
  const { data, error } = await requireSupabase()
    .from('post_event_reports')
    .select('*')
    .eq('event_id', eventId)
    .eq('coach_id', coachId)
    .maybeSingle()
  if (error) throw new Error(error.message)
  return (data as PostEventReport | null) ?? null
}

/**
 * The questionnaire opens when the event ends, not before. The event screen
 * hides the form until then; this keeps a direct call honest too.
 */
function assertEventOver(endsAt: string, now = new Date()): void {
  if (new Date(endsAt).getTime() > now.getTime()) {
    throw new Error('This event has not finished yet. The report opens when it ends.')
  }
}

/**
 * Submit the questionnaire, or correct one already submitted.
 *
 * Only a coach who is actually on the event may file one: that is the RLS
 * policy post_event_reports_insert_own, and the demo store refuses it the same
 * way. An incident must come with details, because "yes something happened"
 * with no description is not a record anyone can act on.
 */
export async function submitPostEventReport(
  input: PostEventReportInput,
): Promise<PostEventReport> {
  if (input.overallRating !== null && (input.overallRating < 1 || input.overallRating > 5)) {
    throw new Error('Rate the event from 1 to 5.')
  }
  if (input.attendanceCount !== null && input.attendanceCount < 0) {
    throw new Error('Attendance cannot be negative.')
  }
  const incidentDetails = input.incidentDetails?.trim() ? input.incidentDetails.trim() : null
  if (input.incident && !incidentDetails) {
    throw new Error('Describe the incident so it can be followed up.')
  }
  const photoPaths = input.photoPaths ?? []

  if (!isSupabaseConfigured) {
    const event = byId(demo().events, input.eventId)
    if (!event) throw new Error('That event no longer exists.')
    assertEventOver(event.ends_at)
    return demoStore.submitPostEventReport({
      eventId: input.eventId,
      coachId: input.coachId,
      overallRating: input.overallRating,
      attendanceCount: input.attendanceCount,
      wentWell: input.wentWell,
      issues: input.issues,
      incident: input.incident,
      incidentDetails,
      clientFeedback: input.clientFeedback,
      photoPaths,
    })
  }

  const sb = requireSupabase()
  const assignRes = await sb
    .from('assignments')
    .select('id, event:events(ends_at)')
    .eq('event_id', input.eventId)
    .eq('coach_id', input.coachId)
    .neq('status', 'cancelled')
    .maybeSingle()
  if (assignRes.error) throw new Error(assignRes.error.message)
  if (!assignRes.data) {
    throw new Error('Only a coach assigned to this event can file its report.')
  }
  const assignment = assignRes.data as unknown as {
    id: string
    event: { ends_at: string } | null
  }
  if (assignment.event) assertEventOver(assignment.event.ends_at)

  const res = await sb
    .from('post_event_reports')
    .upsert(
      {
        event_id: input.eventId,
        coach_id: input.coachId,
        assignment_id: assignment.id,
        submitted_at: new Date().toISOString(),
        overall_rating: input.overallRating,
        attendance_count: input.attendanceCount,
        went_well: input.wentWell,
        issues: input.issues,
        incident: input.incident,
        incident_details: incidentDetails,
        client_feedback: input.clientFeedback,
        photo_paths: photoPaths,
      },
      { onConflict: 'event_id,coach_id' },
    )
    .select()
    .single()
  if (res.error) throw new Error(res.error.message)
  return res.data as PostEventReport
}

/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */

export async function listNotifications(coachId: string): Promise<NotificationQueueRow[]> {
  if (!isSupabaseConfigured) {
    return demo()
      .notificationQueue.filter((n) => n.coach_id === coachId && n.status === 'sent')
      .sort((a, b) => (b.sent_at ?? b.send_at).localeCompare(a.sent_at ?? a.send_at))
      .slice(0, 50)
  }
  const res = await requireSupabase()
    .from('notification_queue')
    .select('*')
    .eq('coach_id', coachId)
    .eq('status', 'sent')
    .order('sent_at', { ascending: false })
    .limit(50)
  return unwrap(res) as NotificationQueueRow[]
}

/**
 * Marks a reminder as seen. This is what keeps a coach off Michel's chase list,
 * so it runs both when the inbox row is opened and when a push notification is
 * tapped (the service worker appends ?n=<id> to the target URL).
 */
export async function acknowledgeNotification(notificationId: string): Promise<void> {
  if (!isSupabaseConfigured) {
    demoStore.acknowledgeNotification(notificationId, demoStore.currentActorId())
    return
  }
  const { error } = await requireSupabase().rpc('acknowledge_notification', {
    p_id: notificationId,
  })
  if (error) throw new Error(error.message)
}

/**
 * Rows the app writes itself, as opposed to the ones the assignment triggers
 * write. Deduplicated on dedupe_key exactly like the SQL, so a repeated tap
 * cannot double-notify.
 *
 * Deliberately non-fatal. notification_queue only accepts inserts from role
 * 'admin', while assigning and approving are open to is_approver(), which also
 * covers the delegated backup approver. So when Rania approves a qualification
 * the approval itself succeeds and this row is refused. Failing the whole call
 * there would report a successful approval as an error, which is worse than a
 * missing in-app notice. Closing the gap properly means an approver insert
 * policy on notification_queue, in a later migration.
 */
async function enqueueAppNotifications(rows: NewNotification[]): Promise<void> {
  if (rows.length === 0) return
  const { error } = await requireSupabase()
    .from('notification_queue')
    .upsert(rows, { onConflict: 'dedupe_key', ignoreDuplicates: true })
  if (error) {
    console.warn('Could not queue the in-app notification:', error.message)
  }
}

/* -------------------------------------------------------------------------- */
/* Push subscriptions                                                         */
/* -------------------------------------------------------------------------- */

export async function savePushSubscription(
  coachId: string,
  subscription: PushSubscriptionJSON,
  platform: string,
): Promise<void> {
  const endpoint = subscription.endpoint
  const p256dh = subscription.keys?.p256dh
  const auth = subscription.keys?.auth
  if (!endpoint || !p256dh || !auth) {
    throw new Error('The browser returned an incomplete push subscription.')
  }

  const row = {
    coach_id: coachId,
    endpoint,
    p256dh,
    auth,
    user_agent: navigator.userAgent,
    platform,
  }

  if (!isSupabaseConfigured) {
    demoStore.savePushSubscription(row)
    return
  }

  const { error } = await requireSupabase()
    .from('push_subscriptions')
    .upsert({ ...row, is_active: true, failure_count: 0 }, { onConflict: 'endpoint' })
  if (error) throw new Error(error.message)
}

export async function deactivatePushSubscription(endpoint: string): Promise<void> {
  if (!isSupabaseConfigured) {
    demoStore.deactivatePushSubscription(endpoint)
    return
  }
  const { error } = await requireSupabase()
    .from('push_subscriptions')
    .update({ is_active: false })
    .eq('endpoint', endpoint)
  if (error) throw new Error(error.message)
}

/* -------------------------------------------------------------------------- */
/* Admin: events and staffing                                                 */
/* -------------------------------------------------------------------------- */

export async function listAdminEvents(): Promise<AdminEventCard[]> {
  if (!isSupabaseConfigured) {
    const db = demo()
    return sortByStart(db.events).map((e) => {
      const assigned = db.assignments.filter(
        (a) => a.event_id === e.id && a.status !== 'cancelled',
      )
      const avail = db.availabilities.filter((a) => a.event_id === e.id)
      return {
        event: withService(db, e),
        assignedCount: assigned.length,
        respondedCount: avail.filter((a) => a.response !== 'pending').length,
        pendingCount: avail.filter((a) => a.response === 'pending').length,
      }
    })
  }

  const sb = requireSupabase()
  const [eventRes, assignRes, availRes] = await Promise.all([
    sb.from('events').select(EVENT_SELECT).order('starts_at'),
    sb.from('assignments').select('event_id, status'),
    sb.from('availabilities').select('event_id, response'),
  ])
  for (const r of [eventRes, assignRes, availRes]) {
    if (r.error) throw new Error(r.error.message)
  }
  const assigns = (assignRes.data ?? []) as Array<{ event_id: string; status: string }>
  const avails = (availRes.data ?? []) as Array<{ event_id: string; response: string }>
  return ((eventRes.data ?? []) as unknown as EventWithService[]).map((event) => ({
    event,
    assignedCount: assigns.filter((a) => a.event_id === event.id && a.status !== 'cancelled')
      .length,
    respondedCount: avails.filter((a) => a.event_id === event.id && a.response !== 'pending')
      .length,
    pendingCount: avails.filter((a) => a.event_id === event.id && a.response === 'pending')
      .length,
  }))
}

export function isUnderstaffed(card: AdminEventCard): boolean {
  return (
    card.event.status !== 'cancelled' &&
    card.event.status !== 'completed' &&
    new Date(card.event.starts_at).getTime() > Date.now() &&
    card.assignedCount < card.event.coaches_required
  )
}

/**
 * The staffing board for one event: every coach qualified for its service, with
 * their answer and whether they are already on it.
 *
 * This is the qualification gate as data. It reads public.event_eligible_coaches
 * (and its demo mirror), which is the one place approved-and-not-revoked is
 * decided. Nothing above this function re-derives it.
 */
export async function listEligibleCoaches(eventId: string): Promise<EligibleCoach[]> {
  if (!isSupabaseConfigured) {
    const db = demo()
    return demoStore
      .eventEligibleCoaches(db, eventId)
      .map((row) => ({
        coach: coachOf(db, row.coach_id),
        qualification_id: row.qualification_id,
        availability_response: row.availability_response,
        responded_at: row.responded_at,
        is_assigned: row.is_assigned,
        assignment_id: row.assignment_id,
      }))
      .sort((a, b) => rankAvailability(b) - rankAvailability(a))
  }

  const sb = requireSupabase()
  const res = await sb.from('event_eligible_coaches').select('*').eq('event_id', eventId)
  const rows = unwrap(res) as EventEligibleCoach[]
  if (rows.length === 0) return []

  const profileRes = await sb
    .from('profiles')
    .select('*')
    .in(
      'id',
      rows.map((r) => r.coach_id),
    )
  if (profileRes.error) throw new Error(profileRes.error.message)
  const profiles = (profileRes.data ?? []) as Profile[]

  return rows
    .map((r) => ({
      // The view already carries the three fields a staffing decision needs, so
      // a missing profile row (RLS, or a departed coach) is not fatal.
      coach:
        profiles.find((p) => p.id === r.coach_id) ??
        ({
          id: r.coach_id,
          full_name: r.full_name,
          phone: r.phone,
          seniority: r.seniority,
        } as Profile),
      qualification_id: r.qualification_id,
      availability_response: r.availability_response,
      responded_at: r.responded_at,
      is_assigned: r.is_assigned,
      assignment_id: r.assignment_id,
    }))
    .sort((a, b) => rankAvailability(b) - rankAvailability(a))
}

/**
 * Put a coach on an event.
 *
 * The gate is checked first, against the eligible list rather than against any
 * rule written here: a coach who is not qualified for the service cannot be
 * assigned to it, full stop. On the real backend the assignment triggers then
 * queue the confirmation and the T-48h / T-24h reminders and compute the draft
 * earning; the demo store mirrors all three.
 *
 * Assigning somebody who is already on the event only sets is_lead. A second
 * tap must not knock a coach who has already confirmed back to 'assigned', nor
 * re-stamp assigned_at, so the reminders they have already been sent stay put.
 */
export async function assignCoach(
  eventId: string,
  coachId: string,
  assignedBy: string,
  options: { isLead?: boolean } = {},
): Promise<Assignment> {
  const isLead = options.isLead ?? false
  const eligible = await listEligibleCoaches(eventId)
  const match = eligible.find((e) => e.coach.id === coachId)
  if (!match) {
    throw new Error(
      'That coach is not qualified for this service yet. Approve the qualification first.',
    )
  }

  if (!isSupabaseConfigured) {
    return demoStore.assignCoach(eventId, coachId, assignedBy, isLead)
  }

  if (match.is_assigned && match.assignment_id) {
    const leadRes = await requireSupabase()
      .from('assignments')
      .update({ is_lead: isLead })
      .eq('id', match.assignment_id)
      .select()
      .single()
    if (leadRes.error) throw new Error(leadRes.error.message)
    return leadRes.data as Assignment
  }

  // Reaching here means there is no live assignment: either none has ever
  // existed, or the coach was stood down and is being put back on. The conflict
  // branch covers the second case, where trg_assignments_after_update re-queues
  // the reminders that were cancelled with it.
  const res = await requireSupabase()
    .from('assignments')
    .upsert(
      {
        event_id: eventId,
        coach_id: coachId,
        status: 'assigned',
        is_lead: isLead,
        assigned_by: assignedBy,
        assigned_at: new Date().toISOString(),
        // A stale confirmation from before the coach was stood down would read
        // as "they have already said yes" on the schedule. They have not.
        confirmed_at: null,
        cancelled_at: null,
        cancellation_reason: null,
      },
      { onConflict: 'event_id,coach_id' },
    )
    .select()
    .single()
  if (res.error) throw new Error(res.error.message)
  await syncEventStaffedStatus(eventId)
  return res.data as Assignment
}

/** Take a coach off an event. Pending reminders die with it. */
export async function unassignCoach(assignmentId: string, reason: string): Promise<Assignment> {
  const row = await cancelAssignmentRow(assignmentId, reason)
  if (isSupabaseConfigured) await syncEventStaffedStatus(row.event_id)
  return row
}

/**
 * events.status follows the headcount: 'staffed' once coaches_required active
 * assignments exist, 'scheduled' otherwise. No trigger does this in the locked
 * migrations, so it is done here after every admin-side assignment change; the
 * demo store applies the same rule inside its own writes.
 *
 * Only is_admin() may update events (events_admin_all), so for a delegated
 * approver the update matches zero rows and the status simply stays put. A
 * coach standing themselves down cannot flip it either: that needs a trigger
 * in a later migration, noted in the handover.
 */
async function syncEventStaffedStatus(eventId: string): Promise<void> {
  const sb = requireSupabase()
  const [eventRes, countRes] = await Promise.all([
    sb.from('events').select('id, status, coaches_required, cancelled_at').eq('id', eventId).single(),
    sb
      .from('assignments')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .neq('status', 'cancelled'),
  ])
  if (eventRes.error) throw new Error(eventRes.error.message)
  if (countRes.error) throw new Error(countRes.error.message)
  const event = eventRes.data as Pick<EventRow, 'id' | 'status' | 'coaches_required' | 'cancelled_at'>
  if (event.cancelled_at !== null) return
  if (event.status !== 'scheduled' && event.status !== 'staffed') return
  const next = (countRes.count ?? 0) >= event.coaches_required ? 'staffed' : 'scheduled'
  if (next === event.status) return
  const upd = await sb.from('events').update({ status: next }).eq('id', eventId)
  if (upd.error) throw new Error(upd.error.message)
}

/* -------------------------------------------------------------------------- */
/* Admin: roster and approvals                                                */
/* -------------------------------------------------------------------------- */

export async function listRoster(): Promise<Profile[]> {
  if (!isSupabaseConfigured) {
    return [...demo().profiles].sort((a, b) => a.full_name.localeCompare(b.full_name))
  }
  const res = await requireSupabase().from('profiles').select('*').order('full_name')
  return unwrap(res) as Profile[]
}

export async function listPendingQualifications(): Promise<PendingQualification[]> {
  if (!isSupabaseConfigured) {
    const db = demo()
    const pending = db.qualifications.filter((q) => q.status === 'pending')
    const out: PendingQualification[] = []
    for (const q of pending) {
      const service = byId(db.services, q.service_id)
      if (!service) continue
      const progress = await getTrainingProgress(q.coach_id)
      out.push({
        ...q,
        coach: coachOf(db, q.coach_id),
        service,
        readiness: progress.readiness.find((r) => r.service_id === q.service_id) ?? null,
      })
    }
    return out.sort((a, b) => a.requested_at.localeCompare(b.requested_at))
  }

  const sb = requireSupabase()
  const res = await sb
    .from('qualifications')
    .select('*, coach:profiles!coach_id(*), service:services(*)')
    .eq('status', 'pending')
    .order('requested_at')
  const rows = unwrap(res) as unknown as Array<Qualification & { coach: Profile; service: Service }>
  if (rows.length === 0) return []

  const readinessRes = await sb
    .from('coach_service_readiness')
    .select('*')
    .in(
      'coach_id',
      rows.map((r) => r.coach_id),
    )
  if (readinessRes.error) throw new Error(readinessRes.error.message)
  const readiness = (readinessRes.data ?? []) as CoachServiceReadiness[]
  return rows.map((r) => ({
    ...r,
    readiness:
      readiness.find((x) => x.coach_id === r.coach_id && x.service_id === r.service_id) ?? null,
  }))
}

/**
 * The human gate. Modules make a coach eligible; this makes them qualified, and
 * from this moment that service's events appear in their schedule.
 *
 * Michel may approve a coach whose module set is not finished. That is
 * deliberate: module 4 has not been extracted from Drive yet, so Camp is a
 * conversation, not a checkbox. The approvals screen shows what is outstanding
 * so the decision is made with the facts in view.
 */
export async function approveQualification(
  qualificationId: string,
  approverId: string,
): Promise<Qualification> {
  if (!isSupabaseConfigured) {
    return demoStore.approveQualification(qualificationId, approverId)
  }

  const sb = requireSupabase()
  const at = new Date().toISOString()
  const res = await sb
    .from('qualifications')
    .update({
      status: 'approved',
      approved_by: approverId,
      approved_at: at,
      // Clearing these matters: is_coach_qualified() requires revoked_at to be
      // null, so re-approving a revoked coach without this would look approved
      // and still show them nothing.
      revoked_at: null,
      revoked_by: null,
      revoke_reason: null,
    })
    .eq('id', qualificationId)
    // Self-approval guard. qualifications_approver_all lets any approver update
    // any row, their own included, so the refusal lives here: the filter makes
    // the update a no-op on the approver's own row and maybeSingle reports it.
    .neq('coach_id', approverId)
    .select('*, service:services(*)')
    .maybeSingle()
  if (res.error) throw new Error(res.error.message)
  if (!res.data) {
    throw new Error(
      `${demoStore.SELF_APPROVAL_MESSAGE} (Or the request is gone: refresh the list.)`,
    )
  }
  const row = res.data as unknown as Qualification & { service: Service }

  await enqueueAppNotifications([
    buildQualificationApprovedNotification(row.coach_id, row.id, row.service, at),
  ])
  const { service: _service, ...qualification } = row
  return qualification
}

/**
 * Decline a request that is not ready. The schema has one "off" state, so this
 * writes 'revoked' with the reason: what separates a decline from a withdrawal
 * on screen is the reason text and whether it was ever approved.
 */
export async function rejectQualification(
  qualificationId: string,
  approverId: string,
  reason: string,
): Promise<Qualification> {
  return turnQualificationOff(qualificationId, approverId, reason, false)
}

/** Withdraw a qualification. The coach stops seeing that service's events. */
export async function revokeQualification(
  qualificationId: string,
  approverId: string,
  reason: string,
): Promise<Qualification> {
  return turnQualificationOff(qualificationId, approverId, reason, true)
}

/**
 * The shared "off" write. The reason is promised to the coach on the admin
 * screen, so it is delivered twice: on the row (revoke_reason, read back by
 * getTrainingProgress) and as an inbox notification carrying the same text.
 * `wasApproved` only shapes the notification copy: a decline of a pending
 * request reads differently from withdrawing something that was granted.
 */
async function turnQualificationOff(
  qualificationId: string,
  approverId: string,
  reason: string,
  wasApproved: boolean,
): Promise<Qualification> {
  const text = reason.trim()
  if (!text) {
    throw new Error('Give a reason. The coach sees it, and fairness needs it.')
  }
  if (!isSupabaseConfigured) {
    // The store decides decline vs withdrawal from the row's previous status.
    return demoStore.revokeQualification(qualificationId, approverId, text)
  }
  const at = new Date().toISOString()
  const res = await requireSupabase()
    .from('qualifications')
    .update({
      status: 'revoked',
      revoked_by: approverId,
      revoked_at: at,
      revoke_reason: text,
    })
    .eq('id', qualificationId)
    .select('*, service:services(*)')
    .single()
  if (res.error) throw new Error(res.error.message)
  const row = res.data as unknown as Qualification & { service: Service }
  await enqueueAppNotifications([
    buildQualificationDecisionNotification(row.coach_id, row.id, row.service, text, wasApproved, at),
  ])
  const { service: _service, ...qualification } = row
  return qualification
}

/** An approved, never-revoked qualification with the coach and service on it. */
export interface ActiveQualification extends Qualification {
  coach: Profile
  service: Service
}

/**
 * Every qualification that is live right now: the revoke list on the approvals
 * screen. Same gate as is_coach_qualified(), approved and not revoked, nothing
 * else. Sorted by coach then service so one person's services sit together.
 */
export async function listApprovedQualifications(): Promise<ActiveQualification[]> {
  if (!isSupabaseConfigured) {
    const db = demo()
    const out: ActiveQualification[] = []
    for (const q of db.qualifications) {
      if (q.status !== 'approved' || q.revoked_at !== null) continue
      const service = byId(db.services, q.service_id)
      if (!service) continue
      out.push({ ...q, coach: coachOf(db, q.coach_id), service })
    }
    return out.sort(
      (a, b) =>
        a.coach.full_name.localeCompare(b.coach.full_name) ||
        a.service.name.localeCompare(b.service.name),
    )
  }

  const res = await requireSupabase()
    .from('qualifications')
    .select('*, coach:profiles!coach_id(*), service:services(*)')
    .eq('status', 'approved')
    .is('revoked_at', null)
    .order('approved_at', { ascending: false })
  if (res.error) throw new Error(res.error.message)
  return res.data as unknown as ActiveQualification[]
}

/* -------------------------------------------------------------------------- */
/* Admin: the chase list                                                      */
/* -------------------------------------------------------------------------- */

/** The types public.notification_chase_list includes. Anything else is not chased. */
const CHASEABLE_TYPES = new Set<NotificationType>([
  'assignment_created',
  'event_reminder_48h',
  'event_reminder_24h',
  'event_updated',
  'event_cancelled',
])

/**
 * Reminders that went out and were never opened. This is the manual WhatsApp
 * safety net the no-WhatsApp-API decision depends on: Michel copies the text
 * and pastes it into the chat he already has with that coach.
 */
export async function listChaseList(): Promise<NotificationChaseRow[]> {
  if (!isSupabaseConfigured) {
    const db = demo()
    return db.notificationQueue
      .filter(
        (n) =>
          (n.status === 'sent' || n.status === 'failed') &&
          !n.acknowledged_at &&
          CHASEABLE_TYPES.has(n.type),
      )
      .map((n) => {
        const coach = coachOf(db, n.coach_id)
        const event = n.event_id ? byId(db.events, n.event_id) : undefined
        return {
          notification_id: n.id,
          coach_id: n.coach_id,
          full_name: coach.full_name,
          phone: coach.phone,
          type: n.type,
          event_id: n.event_id,
          event_title: event?.title ?? null,
          starts_at: event?.starts_at ?? null,
          sent_at: n.sent_at,
          status: n.status,
          // Same shape as notification_chase_list: title, newline, body.
          copy_text: `${n.payload.title}\n${n.payload.body}`,
        }
      })
      // Soonest event first: that is the one Michel has to chase now. Rows
      // without an event fall to the end.
      .sort(
        (a, b) =>
          (a.starts_at ?? '9999').localeCompare(b.starts_at ?? '9999') ||
          (a.sent_at ?? '').localeCompare(b.sent_at ?? ''),
      )
  }
  const res = await requireSupabase()
    .from('notification_chase_list')
    .select('*')
    .order('starts_at', { ascending: true, nullsFirst: false })
    .order('sent_at', { ascending: true })
  return unwrap(res) as NotificationChaseRow[]
}

export async function getAdminDashboard(): Promise<AdminDashboard> {
  const [eventCards, roster, pending, chase] = await Promise.all([
    listAdminEvents(),
    listRoster(),
    listPendingQualifications(),
    listChaseList(),
  ])
  const now = Date.now()
  const upcoming = eventCards.filter(
    (c) =>
      new Date(c.event.starts_at).getTime() > now &&
      c.event.status !== 'cancelled' &&
      c.event.status !== 'completed',
  )
  return {
    eventsNeedingCoaches: upcoming.filter(isUnderstaffed),
    upcomingCount: upcoming.length,
    pendingApprovals: pending.length,
    unacknowledgedReminders: chase.length,
    activeCoaches: roster.filter((p) => p.role === 'coach' && p.status === 'active').length,
    candidateCoaches: roster.filter((p) => p.role === 'coach' && p.status === 'candidate').length,
  }
}
